import { FunctionComponent, useEffect, useRef, useState } from "react";
import "./VideoTutorialMaster.scss";
import Table from "../../Components/Table/Table";
import CustomToolbar from "../../Components/Table/components/CustomToolbar";
import ButtonField from "../../Components/Button/ButtonField";
import { Columns } from "../../Components/Table/ITable";
import { GridRenderCellParams } from "@mui/x-data-grid";
import { IVideoTutorial } from "../VideoPage/IVideoTutorial";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import Dialog from "../../Components/Dialog/Dialog";
import VideoTutorialForm from "./VideoTutorialForm";
import ResponseDialog from "../../Components/ResponseDialog/ResponseDialog";
import TableSearch from "../../Components/Table/components/TableSearch";
import { ReactComponent as Delete } from "../../Assets/Images/delete.svg";
import { ReactComponent as Edit } from "../../Assets/Images/edit.svg";
import { IDeleteDialog } from "../../Components/DeleteDialog/IDeleteDialog";
import IconButtonField from "../../Components/Button/IconButtonField";
import { SvgIcon } from "@mui/material";
import DeleteDialog from "../../Components/DeleteDialog/DeleteDialog";
import { ReactComponent as RefreshButton } from "../../Assets/Images/RefreshButton.svg";
import { listVideoTutorials, clearListVideoTutorials } from "../../Redux/Slices/VideoTutorials/ListVideoTutorialsSlice";
import { clearAddVideoTutorialResponse } from "../../Redux/Slices/VideoTutorials/AddVideoTutorialSlice";
import { clearEditVideoTutorialResponse } from "../../Redux/Slices/VideoTutorials/EditVideoTutorialSlice";
import { deleteVideoTutorial, clearDeleteVideoTutorialResponse } from "../../Redux/Slices/VideoTutorials/DeleteVideoTutorialSlice";
import { listPackageDomain } from "../../Redux/Slices/Package/ListPackageDomainSlice";
import { listVideoTutorialCategory } from "../../Redux/Slices/VideoTutorialCategory/ListVideoTutorialCategorySlice";
import { listYearlevel_map } from "../../Redux/Slices/Yearlevel_map/ListYearlevel_mapSlice";

interface VideoTutorialMasterProps { }

const VideoTutorialMaster: FunctionComponent<VideoTutorialMasterProps> = () => {
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const [deleteDialogData, setDeleteData] = useState<IDeleteDialog>({});
    const [initialValues, setInitialValues] = useState<Partial<IVideoTutorial>>({
        status: true,
    });

    const dispatch = useAppDispatch();
    const { data: listTutorials } = useAppSelector((state) => state.ListVideoTutorialsSlice);
    const { data: addResponse } = useAppSelector((state) => state.AddVideoTutorialSlice);
    const { data: editResponse } = useAppSelector((state) => state.EditVideoTutorialSlice);
    const { data: deleteResponse } = useAppSelector((state) => state.DeleteVideoTutorialSlice);

    const permissions = useAppSelector((state) => state.PermissionsToPage);
    const finaldata = { permissions };

    const formRef = useRef<HTMLFormElement>(null);

    const columns: Columns = [
        {
            field: "index",
            renderHeader: () => <b>S.No</b>,
            width: 70,
            headerAlign: "center",
            align: "center",
        },
        {
            field: "video_title",
            renderHeader: () => <b>Video Title</b>,
            flex: 1,
            type: "string",
            headerAlign: "left",
        },
        {
            field: "domain",
            renderHeader: () => <b>Domain</b>,
            width: 150,
            type: "string",
            headerAlign: "center",
            align: "center",
        },
        {
            field: "video_duration",
            renderHeader: () => <b>Duration</b>,
            width: 120,
            type: "string",
            headerAlign: "center",
            align: "center",
        },
        {
            field: "category_info",
            renderHeader: () => <b>Category</b>,
            width: 200,
            type: "string",
            headerAlign: "left",
            valueGetter: (params) => params.row.category_info?.meta_value || "N/A",
        },
        {
            field: "status",
            renderHeader: () => <b>Status</b>,
            width: 120,
            type: "string",
            headerAlign: "left",
            cellClassName: (params) =>
                `activename ${params.row.status ? "active" : "inactive"}`,
            valueFormatter: (params) => (params.value === true ? "Active" : "Inactive"),
        },
        {
            field: "action",
            renderHeader: () => <b>Action</b>,
            width: 150,
            type: "actions",
            renderCell: (params: GridRenderCellParams<IVideoTutorial>) => {
                return (
                    <>
                        <IconButtonField
                            disabled={finaldata?.permissions?.edit === false}
                            onClick={() => {
                                setInitialValues({ ...params.row });
                                setIsEdit(true);
                                setDialogOpen(true);
                            }}
                        >
                            <SvgIcon sx={{ fill: "none" }} component={Edit} inheritViewBox />
                        </IconButtonField>
                        <IconButtonField
                            disabled={finaldata?.permissions?.delete === false}
                            onClick={() => {
                                setDeleteData({ id: params.id, name: params?.row?.video_title });
                                setDeleteDialogOpen(true);
                            }}
                        >
                            <SvgIcon component={Delete} inheritViewBox />
                        </IconButtonField>
                    </>
                );
            },
        },
    ];

    const rawData = (listTutorials as any)?.data?.video_tutorials;
    const tutorialsData = Array.isArray(rawData) ? rawData : [];
    const rows = tutorialsData.map((tut, index) => ({
        ...tut,
        id: tut.vid_tut_id,
        index: index + 1
    }));

    useEffect(() => {
        dispatch(listVideoTutorials({}));
        dispatch(listPackageDomain());
        dispatch(listVideoTutorialCategory({}));
        dispatch(listYearlevel_map());
    }, [addResponse, editResponse, deleteResponse, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(clearAddVideoTutorialResponse());
            dispatch(clearEditVideoTutorialResponse());
            dispatch(clearDeleteVideoTutorialResponse());
            dispatch(clearListVideoTutorials());
        };
    }, [dispatch]);

    return (
        <>
            <Table rows={rows} columns={columns} exportFileName="VideoTutorials">
                <CustomToolbar>
                    <h6></h6>
                    <TableSearch />
                    <ButtonField
                        className="tabel_add_new_button"
                        onClick={() => {
                            setInitialValues({ status: true });
                            setIsEdit(false);
                            setDialogOpen(true);
                        }}
                        toolTip="Add new"
                        disabled={finaldata?.permissions?.add === false}
                    >
                        Add new
                    </ButtonField>
                    <IconButtonField
                        className="refresh_btn"
                        toolTip="Refresh"
                        onClick={() => dispatch(listVideoTutorials({}))}
                    >
                        <SvgIcon
                            className="refresh_svg_icon"
                            component={RefreshButton}
                            inheritViewBox
                        />
                    </IconButtonField>
                </CustomToolbar>
            </Table>

            <Dialog
                open={dialogOpen}
                dialogClassName="dialog"
                Dialog_Title={`${isEdit ? "Update" : "Add"} Video Tutorial`}
                dialogContentClassName="dialog_content"
                Dialog_Content={
                    <VideoTutorialForm
                        formRef={formRef}
                        initialValues={initialValues}
                        setInitialValues={setInitialValues}
                    />
                }
                Dialog_Actions={
                    <>
                        <ButtonField
                            className="form_cancel_button"
                            onClick={() => {
                                setDialogOpen(false);
                                setIsEdit(false);
                            }}
                        >
                            Cancel
                        </ButtonField>
                        <ButtonField
                            className="form_save_button"
                            onClick={() => formRef.current?.requestSubmit()}
                        >
                            Save
                        </ButtonField>
                    </>
                }
            />
            <ResponseDialog response={addResponse} parentDialog={setDialogOpen} />
            <ResponseDialog response={editResponse} parentDialog={setDialogOpen} />
            <DeleteDialog
                deleteableName={deleteDialogData.name ?? ""}
                open={deleteDialogOpen}
                setOpen={setDeleteDialogOpen}
                deleteFunction={() => {
                    dispatch(deleteVideoTutorial({ vid_tut_id: deleteDialogData.id, is_delete: true }));
                }}
                response={deleteResponse}
            />
        </>
    );
};

export default VideoTutorialMaster;
